/**
 * Gateways tab — three step cards: Gateways, Strategy, and
 * (Phase 36) the Experiment — admin-only unless granted (14/10/2026).
 * Routes come from the country catalog (the automations plan-editor
 * precedent); the pool's array order is the planner's rotation order.
 * The gateway verdict sits under the pool — the same card as on Verdicts.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Rows3 } from 'lucide-react';

import { StepCard, StepCardGroup } from '@/components/ui';
import { useCountry } from '@/hooks';

import type { DraftGateway, DropGenPlanDetail } from '../../../drop-generator.types';
import { useCanSeeExperiments } from '../../shared/experiment/use-can-see-experiments';
import { InheritedSection } from '../master/InheritedSection';
import { rulesSummary } from '../rules/rule-model';
import type { ContainerEditor } from '../state/useContainerEditor';
import { TAB_CHROME } from '../tabs/tab-chrome';
import { GatewayVerdictCard, VerdictHome } from '../verdicts/cards';
import { ExperimentStep } from './experiment/ExperimentStep';
import { PoolStep } from './pool/PoolStep';
import { StrategyStep } from './StrategyStep';
import { STRATEGY_LABELS, gwLetter } from './cards/strategy-copy';
import { poolRouteIds, rankingNeeded } from './ranking/formula-pool';
import { useGatewayRanking } from './ranking/use-gateway-ranking';
import { TestSpreadRow } from './route-test/TestSpreadRow';

interface Props {
  editor: ContainerEditor;
  plan: DropGenPlanDetail;
  /** Null until the container is saved (the experiment needs one). */
  containerId?: string | null;
}

/** "A B C · 3 lanes over 2 gateways · 1 with a daily capacity · 2 with sender IDs" — the step card's line (Phase 40: a row is a lane). */
function poolWords(gw: DraftGateway): string {
  const n = gw.pool.length;
  const gateways = poolRouteIds(gw).length;
  const caps = gw.pool.filter((r) => r.capacity != null).length;
  const withIds = gw.pool.filter((r) => r.senders.ids.length > 0).length;
  return [
    gw.pool.map((_, i) => gwLetter(i)).join(' '),
    gateways === n ? `${n} gateway${n > 1 ? 's' : ''}` : `${n} lanes over ${gateways} gateway${gateways > 1 ? 's' : ''}`,
    caps > 0 ? `${caps} with a daily capacity` : null,
    withIds > 0 ? `${withIds} with sender IDs` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

export const GatewaysTab: FC<Props> = ({ editor, plan, containerId = null }) => {
  const countryKey = plan.countryKey;
  const country = useCountry(countryKey);
  const canSeeExperiments = useCanSeeExperiments();
  const { draft, gatewayOp } = editor;
  const gw = draft.gateway;
  const weighted = gw.strategy === 'RANDOM' || gw.strategy === 'SPLIT';
  const routes = country.data?.routes ?? [];
  const ranking = useGatewayRanking({
    countryKey,
    campaignWiserId: draft.campaignWiserId,
    ranking: gw.ranking,
    routeIds: poolRouteIds(gw),
    enabled: draft.kind !== 'ROUTE_TEST' && rankingNeeded(gw),
  });
  const routeName = (id: number) => routes.find((r) => r.id === id)?.name ?? `Route ${id}`;

  const strategySummary = [
    STRATEGY_LABELS[gw.strategy] ?? gw.strategy,
    weighted
      ? (({ MANUAL: 'manual weights', FORMULA: 'weights by formula' } as Record<string, string>)[gw.weightsMode] ?? 'equal weights')
      : gw.orderMode === 'FORMULA'
        ? 'formula order'
        : 'drag order',
    rulesSummary(gw.ranking.rules),
  ]
    .filter(Boolean)
    .join(' · ');

  const poolSummary = gw.pool.length === 0 ? 'no gateways yet' : poolWords(gw);

  const splitOver = draft.slots.filter((s) => s.enabled && s.routeId === null).length;

  if (draft.kind === 'ROUTE_TEST')
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">
          The route test sends small test drops through these gateways to find the one that delivers best. When the test is over, the route
          test formula picks the winner.
        </p>
        <InheritedSection section="gatewayPool">
          <PoolStep countryKey={countryKey} gateway={gw} routes={routes} onOp={gatewayOp} />
        </InheritedSection>
        <InheritedSection section="gatewayStrategy">
          <TestSpreadRow
            strategy={gw.strategy}
            slots={draft.slots}
            gatewayCount={gw.pool.length}
            onChange={(strategy) => gatewayOp({ kind: 'patch', patch: { strategy, orderMode: 'MANUAL', weightsMode: 'OFF' } })}
          />
        </InheritedSection>
      </div>
    );
  return (
    <div className="flex flex-col gap-4">
      <StepCardGroup>
        <StepCard index={1} icon={Rows3} title="Gateways" summary={poolSummary}>
          <InheritedSection section="gatewayPool">
            <PoolStep countryKey={countryKey} gateway={gw} routes={routes} onOp={gatewayOp} />
          </InheritedSection>
        </StepCard>
        <StrategyStep
          editor={editor}
          plan={plan}
          containerId={containerId}
          countryKey={countryKey}
          summary={strategySummary}
          splitOver={splitOver}
          ranking={ranking}
          routeName={routeName}
        />
        {canSeeExperiments ? (
          <ExperimentStep editor={editor} countryKey={countryKey} containerId={containerId} routeName={routeName} />
        ) : null}
      </StepCardGroup>
      <VerdictHome
        title="Gateway verdict"
        hint="This rule judges the gateways in the pool above. The same rule as on Verdicts."
        titleClass={TAB_CHROME.gateways.heading}
      >
        <GatewayVerdictCard index={1} editor={editor} plan={plan} containerId={containerId} />
      </VerdictHome>
    </div>
  );
};
